import { useParams, Link } from 'react-router-dom';
import { categories, categoryUrlData, articles } from '@/mocks/navigation';
import Breadcrumb from '@/components/base/Breadcrumb';

function StatusBadge({ status }: { status: string }) {
  const isOnline = status === '正常';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
        isOnline
          ? 'bg-accent-100 text-accent-700'
          : 'bg-secondary-100 text-secondary-700'
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${isOnline ? 'bg-accent-500' : 'bg-secondary-400'}`}></span>
      {status}
    </span>
  );
}

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const urls = categoryUrlData[slug || ''] || [];
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50 pt-20">
        <div className="container-custom py-20 text-center">
          <h1 className="text-2xl font-bold text-foreground-950">分类未找到</h1>
          <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50 pt-16">
      <div className="container-custom py-8">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: category.name },
          ]}
        />

        {/* Header */}
        <div className="mt-4">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground-950">
            {category.name}
          </h1>
          <p className="mt-2 text-base text-foreground-500 max-w-2xl">
            {category.description}
          </p>
        </div>

        {/* Category Tabs */}
        <div className="mt-8 flex flex-wrap items-center gap-2">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                cat.slug === slug
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              {cat.name}
              <span className="ml-1 text-xs opacity-70">({cat.count})</span>
            </Link>
          ))}
        </div>

        {/* URL List */}
        {urls.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-bold text-foreground-950 mb-4">可用网址列表</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-product-shop>
              {urls.map((item) => (
                <a
                  key={item.id}
                  href={item.url}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="group block p-5 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 truncate">
                        {item.title}
                      </h3>
                      <p className="mt-1 text-sm text-foreground-500 truncate">{item.url}</p>
                    </div>
                    <StatusBadge status={item.status} />
                  </div>
                  <div className="mt-4 flex items-center justify-between text-xs text-foreground-400">
                    <span className="flex items-center gap-1">
                      <i className="ri-speed-line"></i>
                      {item.speed}
                    </span>
                    <span>更新于 {item.updatedAt}</span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Related Articles */}
        {categoryArticles.length > 0 && (
          <div className="mt-12">
            <h2 className="text-xl font-bold text-foreground-950 mb-4">相关文章</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categoryArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group block bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-200 overflow-hidden transition-all duration-300"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="mt-2 text-sm text-foreground-500 line-clamp-2 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="mt-4 flex items-center justify-between text-xs text-foreground-400">
                      <span>{article.publishedAt}</span>
                      <span>{article.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Empty State for tutorials */}
        {slug === 'tutorials' && (
          <div className="mt-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {articles.filter(a => a.category === 'tutorials').map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group block bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-200 overflow-hidden transition-all duration-300"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {article.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="px-2 py-0.5 bg-accent-100 text-accent-700 text-xs font-medium rounded-full whitespace-nowrap">
                          {tag}
                        </span>
                      ))}
                      <span className="text-xs text-foreground-400">{article.readTime}</span>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="mt-2 text-sm text-foreground-500 line-clamp-2 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="mt-4 text-xs text-foreground-400">{article.publishedAt}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}